import INITIAL_FILTERS from '@/retention/retention-filters/initial-filters.js';
import moment from '@utils/moment-wrapper';

export function getMoment (date, cohortSize) {
    const momentObj = moment(date, { ignoreTimeZone: true });
    if (cohortSize) momentObj.endOf(cohortSize);

    return momentObj;
}

export function getPeriodStart (cohortSize, periodCount) {
    return getMoment()
        .subtract(periodCount, cohortSize)
        .startOf(cohortSize);
}

export function getPeriodEnd (cohortSize, subtrahend = 1) {
    return getMoment()
        .subtract(subtrahend, cohortSize)
        .endOf(cohortSize);
}

export function getPeriodFilterFormat (cohortSize) {
    if (cohortSize === 'month') return 'YYYY-MM';

    return 'YYYY-MM-DD';
}

export function padCustomWeeklyPeriod (period) {
    return period.clone().add(6, 'days');
}

/**
 * Span sent to retention aggregations as `periodCount` (see retrieveCohortData). The pipeline sets
 * `timeSeries.count` to this value + 1.
 *
 * Collapse to 0 when the UI range is a single cohort bucket (same as legacy inverted defaults where
 * diff+1 was 0 → timeSeries.count 1).
 */
export function getRetentionAggregationPeriodCount (periodStart, periodEnd, cohortSize) {
    const start = moment(periodStart, { ignoreTimeZone: true });
    const end = moment(periodEnd, { ignoreTimeZone: true });
    const span = end.diff(start, cohortSize) + 1;

    if (span <= 0) {
        return 0;
    }

    if (span === 1 && start.clone().startOf(cohortSize).isSame(end.clone().startOf(cohortSize))) {
        return 0;
    }

    return span;
}

export function getDefaultPeriodFilters (firstEventTime, cohortSize = INITIAL_FILTERS.cohortSize) {
    let { periodStart, periodEnd, periodCount } = INITIAL_FILTERS;
    if (cohortSize === 'day') periodCount = 7;
    let defaultPeriodStart;
    const firstEventPeriodStart = moment(firstEventTime, { ignoreTimeZone: true }).startOf(cohortSize);
    defaultPeriodStart = getPeriodStart(cohortSize, periodCount);

    if (firstEventPeriodStart.isAfter(defaultPeriodStart)) {
        defaultPeriodStart = firstEventPeriodStart;
        periodCount = 'custom';
    }

    let endMoment = getPeriodEnd(cohortSize);
    const startPeriodEnd = defaultPeriodStart.clone().endOf(cohortSize);
    const latestAllowedEnd = getPeriodEnd(cohortSize, 0);

    // If first-event clamp moved the window forward, raw period end can fall before the start cohort
    // (e.g. February end vs March start). Align end to that cohort, capped at the latest allowed instant.
    if (endMoment.isBefore(startPeriodEnd)) {
        endMoment = startPeriodEnd.clone();
        if (endMoment.isAfter(latestAllowedEnd)) {
            endMoment = latestAllowedEnd.clone();
        }
    }

    periodStart = defaultPeriodStart.format(getPeriodFilterFormat(cohortSize));
    periodEnd = endMoment.format(getPeriodFilterFormat(cohortSize));

    return { periodStart, periodEnd, periodCount };
}
